import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '@/store';
import { fetchISOwnerDashboard } from '@/store/slices/dashboardSlice';
import { fetchInvestibleStructures } from '@/store/slices/investibleStructuresSlice';
import { fetchPlanningPeriods } from '@/store/slices/planningPeriodsSlice';
import { fetchScenarios } from '@/store/slices/scenariosSlice';
import { formatNumber, formatPercent } from '@/utils/formatters';

// Design System Components
import {
  GlassCard,
  GlassPageHeader,
  GlassSelect,
  GlassLoadingSpinner,
  SectionReveal,
} from '@/design-system';

// Components
import CapacitySummarySection from './components/CapacitySummarySection';
import ContributingProgramsView from './components/ContributingProgramsView';

// Icons
import { Layers, CheckCircle2, LayoutList, Target, ArrowRight, type LucideIcon } from 'lucide-react';

// Navigation Metric Card Component
interface NavigationMetricCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  href: string;
  color: 'blue' | 'emerald' | 'violet' | 'amber';
  subtitle?: string;
}

const colorConfig = {
  blue: {
    bg: 'from-accent-blue/20 to-accent-blue/5',
    icon: 'text-accent-blue',
    border: 'border-accent-blue/20 hover:border-accent-blue/40',
    ring: 'focus:ring-accent-blue/50',
  },
  emerald: {
    bg: 'from-accent-emerald/20 to-accent-emerald/5',
    icon: 'text-accent-emerald',
    border: 'border-accent-emerald/20 hover:border-accent-emerald/40',
    ring: 'focus:ring-accent-emerald/50',
  },
  violet: {
    bg: 'from-accent-violet/20 to-accent-violet/5',
    icon: 'text-accent-violet',
    border: 'border-accent-violet/20 hover:border-accent-violet/40',
    ring: 'focus:ring-accent-violet/50',
  },
  amber: {
    bg: 'from-accent-amber/20 to-accent-amber/5',
    icon: 'text-accent-amber',
    border: 'border-accent-amber/20 hover:border-accent-amber/40',
    ring: 'focus:ring-accent-amber/50',
  },
};

const NavigationMetricCard: React.FC<NavigationMetricCardProps> = ({
  title,
  value,
  icon: Icon,
  href,
  color,
  subtitle = 'View all',
}) => {
  const navigate = useNavigate();
  const config = colorConfig[color];

  const handleClick = () => {
    navigate(href);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(href);
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`${title}: ${value}. ${subtitle}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        group relative cursor-pointer
        bg-gradient-to-br ${config.bg}
        backdrop-blur-sm rounded-xl
        border ${config.border}
        p-5 transition-all duration-300 ease-out
        hover:shadow-lg hover:shadow-black/10
        hover:-translate-y-0.5
        focus:outline-none focus:ring-2 ${config.ring}
      `}
    >
      {/* Arrow Icon */}
      <ArrowRight className="absolute top-4 right-4 w-4 h-4 text-muted-foreground/60 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1" />

      {/* Icon */}
      <div className={`${config.icon} mb-3 transition-transform duration-300 group-hover:scale-105`}>
        <Icon className="w-7 h-7" />
      </div>

      {/* Value */}
      <div className="text-3xl font-bold text-foreground tabular-nums mb-1">
        {formatNumber(value)}
      </div>

      {/* Title */}
      <div className="text-sm font-medium text-muted-foreground mb-2">{title}</div>

      {/* Subtitle with arrow hint */}
      <div className="text-xs text-muted-foreground/60 flex items-center gap-1 group-hover:text-muted-foreground transition-colors">
        {subtitle}
        <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
      </div>
    </div>
  );
};

const ISOwnerDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isOwner } = useSelector((state: RootState) => state.dashboard);
  const dashboardLoading = isOwner.isLoading;
  const { structures, isLoading: structuresLoading } = useSelector((state: RootState) => state.investibleStructures);
  const isLoading = dashboardLoading || structuresLoading;
  const { periods: planningPeriods } = useSelector((state: RootState) => state.planningPeriods);
  const { scenarios } = useSelector((state: RootState) => state.scenarios);
  const { user } = useSelector((state: RootState) => state.auth);

  const [selectedIS, setSelectedIS] = useState<string>('');
  const [selectedPlanningPeriod, setSelectedPlanningPeriod] = useState<string>('');
  const [selectedScenario, setSelectedScenario] = useState<string>('');

  // Reset local selections when user changes (handles logout → login without unmount)
  useEffect(() => {
    setSelectedIS('');
    setSelectedPlanningPeriod('');
    setSelectedScenario('');
  }, [user?.id]);

  useEffect(() => {
    dispatch(fetchPlanningPeriods());
    dispatch(fetchInvestibleStructures({}));
  }, [dispatch]);

  // Auto-select first IS when structures are loaded (for IS_OWNER users who only see their owned IS)
  useEffect(() => {
    if (structures?.length > 0 && !selectedIS) {
      setSelectedIS(structures[0].id);
    }
  }, [structures, selectedIS]);

  useEffect(() => {
    if (selectedPlanningPeriod) {
      dispatch(fetchScenarios({ planningPeriodId: selectedPlanningPeriod }));
    }
  }, [dispatch, selectedPlanningPeriod]);

  useEffect(() => {
    if (planningPeriods?.length > 0 && !selectedPlanningPeriod) {
      const activePeriod = planningPeriods.find(p => p.isActive);
      setSelectedPlanningPeriod(activePeriod?.id || planningPeriods[0].id);
    }
  }, [planningPeriods, selectedPlanningPeriod]);

  useEffect(() => {
    if (scenarios?.length > 0 && !selectedScenario) {
      const baselineScenario = scenarios.find(s => s.isBaseline);
      setSelectedScenario(baselineScenario?.id || scenarios[0].id);
    }
  }, [scenarios, selectedScenario]);

  useEffect(() => {
    if (selectedIS && selectedPlanningPeriod) {
      dispatch(fetchISOwnerDashboard({
        investibleStructureId: selectedIS,
        planningPeriodId: selectedPlanningPeriod,
        scenarioId: selectedScenario || undefined,
      }));
    }
  }, [dispatch, selectedIS, selectedPlanningPeriod, selectedScenario]);

  const isOptions = [
    { value: '', label: 'Select Investible Structure' },
    ...(structures || []).map(is => ({
      value: is.id,
      label: `${is.name} (${is.category})`,
    })),
  ];

  const planningPeriodOptions = [
    { value: '', label: 'Select Planning Period' },
    ...(planningPeriods || []).map(period => ({
      value: period.id,
      label: `${period.name}${period.isActive ? ' (Active)' : ''}`,
    })),
  ];

  const scenarioOptions = [
    { value: '', label: 'All Scenarios' },
    ...(scenarios || []).map(scenario => ({
      value: scenario.id,
      label: `${scenario.name}${scenario.isBaseline ? ' (Baseline)' : ''}`,
    })),
  ];

  const selectedISData = structures?.find(is => is.id === selectedIS);

  return (
    <div className="glass-page-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header Section */}
        <SectionReveal delay={0}>
          <GlassPageHeader
            title="IS Owner Dashboard"
            subtitle={selectedISData ?
              `Portfolio view for ${selectedISData.name} (${selectedISData.category})` :
              'Select an Investible Structure to view portfolio details'
            }
            icon={<Layers className="w-6 h-6" />}
            iconGradient="blue-violet"
            className="mb-6"
            actions={
              <div className="flex items-center gap-3 flex-wrap">
                <GlassSelect
                  value={selectedIS}
                  onChange={(value) => setSelectedIS(value)}
                  options={isOptions}
                  size="sm"
                />
                <GlassSelect
                  value={selectedPlanningPeriod}
                  onChange={(value) => setSelectedPlanningPeriod(value)}
                  options={planningPeriodOptions}
                  size="sm"
                />
                <GlassSelect
                  value={selectedScenario}
                  onChange={(value) => setSelectedScenario(value)}
                  options={scenarioOptions}
                  size="sm"
                />
              </div>
            }
          />
        </SectionReveal>

        {isLoading || !structures?.length ? (
          <SectionReveal delay={0.1}>
            <GlassCard padding="lg">
              <div className="p-12 flex flex-col items-center justify-center">
                <GlassLoadingSpinner size="lg" variant="gradient" label="Loading dashboard data..." />
              </div>
            </GlassCard>
          </SectionReveal>
        ) : !selectedIS ? (
          <SectionReveal delay={0.1}>
            <GlassCard padding="lg">
              <div className="p-12 text-center">
                <div className="text-muted-foreground/60 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">Select an Investible Structure</h3>
                <p className="text-muted-foreground/60">
                  Choose an Investible Structure from the dropdown above to view capacity summary,
                  feature readiness, EPICs, and contributing programs.
                </p>
              </div>
            </GlassCard>
          </SectionReveal>
        ) : (
          <>
            {/* Capacity Summary - SectionReveal is handled internally */}
            <SectionReveal delay={0.1}>
              <CapacitySummarySection
                capacitySummary={isOwner.capacitySummary}
                isLoading={isLoading}
              />
            </SectionReveal>

            {/* Contributing Programs - Full Width */}
            <SectionReveal delay={0.2}>
              <div className="mb-6">
                <ContributingProgramsView
                  contributingPrograms={isOwner.contributingPrograms}
                  isLoading={isLoading}
                />
              </div>
            </SectionReveal>

            {/* Portfolio Navigation */}
            <SectionReveal delay={0.3}>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Portfolio Overview</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <NavigationMetricCard
                    title="Total EPICs"
                    value={isOwner.epics?.length || 0}
                    icon={Layers}
                    href="/epics"
                    color="blue"
                  />
                  <NavigationMetricCard
                    title="Committed EPICs"
                    value={isOwner.epics?.filter(e => e.status === 'COMMITTED').length || 0}
                    icon={CheckCircle2}
                    href="/epics"
                    color="emerald"
                  />
                  <NavigationMetricCard
                    title="Total Features"
                    value={isOwner.featureReadiness?.reduce((sum, q) => sum + q.totalFeatures, 0) || 0}
                    icon={LayoutList}
                    href="/features"
                    color="violet"
                  />
                  <NavigationMetricCard
                    title="Total OKRs"
                    value={isOwner.okrCount || 0}
                    icon={Target}
                    href="/okrs"
                    color="amber"
                  />
                </div>
              </div>
            </SectionReveal>

            {/* Portfolio Health Summary */}
            <SectionReveal delay={0.4}>
              <GlassCard padding="lg">
                <h3 className="text-lg font-semibold text-foreground mb-4">Portfolio Health Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground tabular-nums">
                      {formatPercent(isOwner.capacitySummary?.utilizationPercentage, { fallback: '0%' })}
                    </div>
                    <div className="text-sm text-muted-foreground/60">Capacity Utilization</div>
                    <div className={`text-xs mt-1 ${
                      (isOwner.capacitySummary?.utilizationPercentage || 0) > 100 ? 'text-accent-rose' :
                      (isOwner.capacitySummary?.utilizationPercentage || 0) > 90 ? 'text-accent-amber' : 'text-accent-emerald'
                    }`}>
                      {(isOwner.capacitySummary?.utilizationPercentage || 0) > 100 ? 'Over-allocated' :
                       (isOwner.capacitySummary?.utilizationPercentage || 0) > 90 ? 'High utilization' : 'Good utilization'}
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground tabular-nums">
                      {isOwner.epics?.filter(epic => ['COMMITTED', 'STRETCH'].includes(epic.status)).length || 0}
                    </div>
                    <div className="text-sm text-muted-foreground/60">Committed EPICs</div>
                    <div className="text-xs text-muted-foreground/60 mt-1">
                      {isOwner.epics?.filter(epic => epic.status === 'STRETCH').length || 0} stretch goals
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground tabular-nums">
                      {isOwner.featureReadiness?.reduce((sum, q) => sum + q.totalFeatures, 0) || 0}
                    </div>
                    <div className="text-sm text-muted-foreground/60">Total Features</div>
                    <div className="text-xs text-muted-foreground/60 mt-1">
                      {isOwner.featureReadiness?.reduce((sum, q) => sum + q.readyFeatures, 0) || 0} ready
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground tabular-nums">
                      {isOwner.contributingPrograms?.length || 0}
                    </div>
                    <div className="text-sm text-muted-foreground/60">Contributing Programs</div>
                    <div className="text-xs text-muted-foreground/60 mt-1">
                      Cross-program coordination
                    </div>
                  </div>
                </div>
              </GlassCard>
            </SectionReveal>
          </>
        )}
      </div>
    </div>
  );
};

export default ISOwnerDashboardPage;
